import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';
import ProgressBar from '../components/ProgressBar.jsx';

const MODES = [
  {
    to: '/lernen/quiz',
    icon: '📝',
    title: 'Quiz',
    desc: 'Typische Prüfungsfragen beantworten — thematisch oder gemischt. Mit direktem Feedback und Erklärung.',
    gradient: 'from-teal-900 via-teal-700 to-teal-400',
    depth: '10 – 20 m',
  },
  {
    to: '/lernen/karteikarten',
    icon: '🗃️',
    title: 'Karteikarten',
    desc: 'Spaced-Repetition-System: Karten werden je nach Lernerfolg kürzer oder länger wiederholt.',
    gradient: 'from-sky-900 via-sky-700 to-sky-400',
    depth: '20 – 30 m',
  },
  {
    href: 'https://scuba.gorman.monster',
    icon: '🤿',
    title: 'Tauchsimulator',
    desc: 'Simulator mit Bühlmann-Deko und CCR-Modus — technisches Tauchen ohne Risiko.',
    gradient: 'from-navy-900 via-navy-800 to-ocean-900',
    depth: '> 30 m',
  },
];

function ModeCard({ m }) {
  const inner = (
    <div className="relative rounded-xl overflow-hidden shadow-md group cursor-pointer transition-transform hover:-translate-y-1 hover:shadow-xl">
      {/* Hintergrund-Gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${m.gradient} opacity-90`} />
      {/* Blasen-Deko */}
      <div className="absolute bottom-3 right-4 flex gap-1.5 opacity-20 group-hover:opacity-40 transition-opacity">
        <span className="block w-2 h-2 rounded-full bg-white animate-bubble-1" />
        <span className="block w-1.5 h-1.5 rounded-full bg-white animate-bubble-2" />
        <span className="block w-1 h-1 rounded-full bg-white animate-bubble-3" />
      </div>
      {/* Tiefenmarker */}
      <div className="absolute top-3 right-3 font-mono text-xs text-white/50 tracking-widest">{m.depth}</div>
      {/* Inhalt */}
      <div className="relative z-10 p-5">
        <div className="text-3xl mb-3" aria-hidden="true">{m.icon}</div>
        <h2 className="font-bold text-white text-base mb-1 group-hover:text-ocean-200 transition-colors">{m.title}</h2>
        <p className="text-sm text-white/70 leading-relaxed">{m.desc}</p>
      </div>
    </div>
  );

  if (m.href) {
    return <a href={m.href} target="_blank" rel="noopener noreferrer">{inner}</a>;
  }
  return <Link to={m.to}>{inner}</Link>;
}

function LernreiheCard({ reihe }) {
  return (
    <Link to={`/lernen/lernreihe/${reihe.id}`}
      className="card hover:border-ocean-300 hover:shadow-md transition-all block space-y-2">
      <div className="flex items-start gap-3">
        <span className="text-xl mt-0.5" aria-hidden="true">🎓</span>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{reihe.title}</p>
          {reihe.description && (
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{reihe.description}</p>
          )}
        </div>
      </div>
      <ProgressBar done={reihe.done_steps} total={reihe.total_steps} />
    </Link>
  );
}

const LINKS = [
  { href: 'https://www.nlbk.niedersachsen.de/startseite/brandschutz/rechtsgrundlagen_brandschutz/feuerwehr_dienstvorschriften/downloadbereich-feuerwehr-dienstvorschriften-156921.html', label: 'FwDV 8 (Niedersachsen)', desc: 'Download-Seite der FwDV 8 „Tauchen" — NLBK Niedersachsen' },
  { href: 'https://publikationen.dguv.de/regelwerk/dguv-vorschriften/1507/feuerwehren', label: 'DGUV Vorschrift 49', desc: 'Unfallverhütungsvorschrift Feuerwehren — DGUV Publikationen' },
  { href: 'https://www.daneurope.org', label: 'DAN Europe', desc: 'Divers Alert Network — Tauchmedizin & Notfallberatung' },
  { href: 'https://www.gtuem.org', label: 'GT Üm e.V.', desc: 'Gesellschaft für Tauch- und Überdruckmedizin' },
];

export default function Lernbereich() {
  const [reihen, setReihen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.getLernreihen()
      .then((d) => setReihen(d.lernreihen ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Lernbereich</h1>
        <p className="text-sm text-gray-500 mt-1">Wähle einen Lernmodus für deine Prüfungsvorbereitung.</p>
      </div>

      {/* Lernreihen — geführte Lernpfade mit Fortschritt */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-widest text-ocean-600 mb-3">Lernreihen</h2>

        {loading && <div className="flex justify-center py-8"><Spinner /></div>}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            Lernreihen konnten nicht geladen werden: {error}
          </div>
        )}

        {!loading && !error && reihen.length === 0 && (
          <div className="card text-center py-8 text-sm text-gray-500">Noch keine Lernreihen vorhanden.</div>
        )}

        {reihen.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {reihen.map((r) => <LernreiheCard key={r.id} reihe={r} />)}
          </div>
        )}
      </div>

      {/* Weitere Lernmodi */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-widest text-ocean-600 mb-3">Üben</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {MODES.map((m) => <ModeCard key={m.href ?? m.to} m={m} />)}
        </div>
      </div>

      {/* Theorie-Materialien */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-widest text-ocean-600 mb-3">Weitere Materialien</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
              className="card hover:border-ocean-300 hover:shadow-md transition-all flex items-start gap-3 group">
              <span className="text-ocean-400 text-lg mt-0.5" aria-hidden="true">↗</span>
              <div>
                <p className="text-sm font-semibold text-gray-900 group-hover:text-ocean-700">{l.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{l.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
